import { Fragment, useState } from "react";
import { toFixedWithoutRounding } from "../../Utils/Constant";
import { asList, sameId } from "./useSportAnalytics";

const transparentImage = "data:image/gif;base64,R0lGODdhAQABAIAAAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICTAEAOw==";
const isBack = (odd) => odd.ib === true || odd.ib === 1;
const isSuspended = (status) => [2, 3, 4, 9].includes(Number(status));

function Position({ value, liability = false }) {
  if (value == null || value === "") return null;
  // Exchange/bookmaker positions use the opposite sign from fancy positions.
  const amount = liability ? -Number(value) : Number(value);
  return <span className={amount < 0 ? "red" : "green"}>
    {amount < 0 ? `(${toFixedWithoutRounding(Math.abs(amount))})` : toFixedWithoutRounding(amount)}
  </span>;
}

function MarketControls({ title, limits, open, onToggle }) {
  return <div className="analytics-market-controls" title={`Min: ${limits?.minAmount ?? 100} | Max: ${limits?.maxAmount ?? 100000}`}>
    <span className="analytics-limits">Min: {limits?.minAmount ?? 100} | Max: {limits?.maxAmount ?? 100000}</span>
    <button type="button" className="btn analytics-collapse" aria-expanded={open}
      aria-label={`${open ? "Collapse" : "Expand"} ${title}`} onClick={onToggle}>
      {open ? "−" : "+"}
    </button>
  </div>;
}

function MarketColumns() {
  return <colgroup><col style={{ width: "40%" }} /><col span={6} style={{ width: "10%" }} /></colgroup>;
}

function SpecialMarketHeading({ title, limits, open, onToggle }) {
  return <tbody><tr className="special_bet">
    <td colSpan={6}><h3 className="analytics-special-title"><p>{title}</p></h3></td>
    <td className="border-l"><MarketControls title={title} limits={limits} open={open} onToggle={onToggle} /></td>
  </tr></tbody>;
}

function runnerMarket(runner, rates, positions, marketId, bookmaker) {
  const selectionId = runner.SelectionId ?? runner.selectionId;
  const name = runner.RunnerName || runner.runnerName;
  const runnerRates = rates.filter((odd) => sameId(odd.ri, selectionId) ||
    (bookmaker && odd.runnerName === name));
  const position = asList(positions).find((item) =>
    sameId(item.selectionId, bookmaker ? `B${selectionId}` : selectionId) &&
    (!item.marketId || sameId(item.marketId, marketId)),
  )?.position;
  return { selectionId, name, back: runnerRates.filter(isBack), lay: runnerRates.filter((odd) => !isBack(odd)), position };
}

function BookmakerMarket({ title, marketId, runners, odds, positions, limits }) {
  const [open, setOpen] = useState(true);
  return <section className="analytics-market" aria-label={title}>
    <div className="bets-wrap bookmaker_bet">
      <table className="bets bets-bookmaker table-responsive">
        <MarketColumns />
        <SpecialMarketHeading title={title} limits={limits} open={open} onToggle={() => setOpen(!open)} />
        {open && <tbody>
          <tr className="bet-all"><td colSpan={3} /><td>Back</td><td>Lay</td><td colSpan={2} className="refer-book" /></tr>
          {runners.map((runner) => {
            const { selectionId, name, back, lay, position } = runnerMarket(runner, asList(odds?.rt), positions, marketId, true);
            return <Fragment key={selectionId || name}>
              {isSuspended(odds?.ms) && <tr className="fancy-suspend-tr">
                <th /><td colSpan={6} className="fancy-suspend-td"><div className="fancy-suspend"><span>Suspend</span></div></td>
              </tr>}
              <tr>
                <th className="predict"><p>{name}<span className="analytics-position"><Position value={position} liability /></span></p></th>
                <td colSpan={3}><dl className="back-gradient">
                  <dd><p /></dd><dd><p /></dd><dd><strong className="analytics-gradient-price">{back[0]?.rt ?? "-"}</strong></dd>
                </dl></td>
                <td colSpan={3}><dl className="lay-gradient">
                  <dd><strong className="analytics-gradient-price">{lay[0]?.rt ?? "-"}</strong></dd><dd><p /></dd><dd><p /></dd>
                </dl></td>
              </tr>
            </Fragment>;
          })}
        </tbody>}
      </table>
    </div>
  </section>;
}

export function OddsMarket(props) {
  return props.bookmaker ? <BookmakerMarket {...props} /> : <ExchangeMarket {...props} />;
}

function ExchangeMarket({ title, marketId, runners, odds, positions, limits, toss = false }) {
  const [open, setOpen] = useState(true);
  const rates = asList(odds?.rt);
  return <section className="analytics-market for-agent" aria-label={title}>
    <div className="head-bets-agent">
      <dl className="game-matched"><dt>{title}</dt></dl>
      {!toss && <dl className="game-matched"><dt>Matched{odds?.totalMatched != null ? `: ${toFixedWithoutRounding(odds.totalMatched)}` : ""}</dt></dl>}
      <MarketControls title={title} limits={limits} open={open} onToggle={() => setOpen(!open)} />
    </div>
    {open && <div className="bets-wrap">
      <span className="refer_only">{runners.length} Selections</span>
      <table className={`bets table-responsive${toss ? " analytics-toss-table" : ""}`}>
        <tbody><tr className="bet-all">
          <td />
          {!toss && <td colSpan={2} className="refer-bet">0.0%</td>}
          <td><div className="back-all"><span>{toss ? "Back" : "Back all"}</span></div></td>
          {!toss && <><td><div className="lay-all"><span>Lay all</span></div></td><td colSpan={2} className="refer-book" style={{ textAlign: "right" }}>0.0%</td></>}
        </tr></tbody>
        <tbody>{runners.map((runner) => {
          const { selectionId, name, back, lay, position } = runnerMarket(runner, rates, positions, marketId, false);
          return <tr key={selectionId || name}>
            <th><p><img src={transparentImage} className="icon-predict" alt="" />{name}
              <span className="analytics-position"><Position value={position} liability /></span>
            </p></th>
            {isSuspended(odds?.ms) ? <td colSpan={toss ? 1 : 6} className="analytics-suspended">Suspended</td> :
              (toss ? [{ side: "back", index: 0, odd: back[0] }] : [
                { side: "back", index: 2, odd: back[2] }, { side: "back", index: 1, odd: back[1] },
                { side: "back", index: 0, odd: back[0] }, { side: "lay", index: 0, odd: lay[0] },
                { side: "lay", index: 1, odd: lay[1] }, { side: "lay", index: 2, odd: lay[2] },
              ]).map(({ side, index, odd }) => <td key={`${side}-${index}`} className={`${side}-${index + 1}`}>
                <strong className="analytics-rate">{odd?.rt ?? "-"}</strong>
                {!toss && <span>{odd?.bv ?? ""}</span>}
              </td>)}
          </tr>;
        })}</tbody>
      </table>
    </div>}
  </section>;
}

export function FancyMarket({ data, positions, limits, onBook }) {
  const [open, setOpen] = useState(true);
  return <section className="analytics-market" aria-label="Fancy">
    <div className="bets-wrap fancy_bet">
      <table className="bets table-responsive">
        <MarketColumns />
        <SpecialMarketHeading title="Fancy" limits={limits} open={open} onToggle={() => setOpen(!open)} />
        {open && <tbody>
          <tr className="bet-all"><td /><td colSpan={2} className="refer-bet" /><td>No</td><td>Yes</td><td colSpan={2} className="refer-book" /></tr>
          {data.map((fancy) => {
            const name = fancy.fancyName || fancy.name;
            const rates = asList(fancy.rt || fancy.odds?.rt);
            const no = rates.find((odd) => odd.ib === false || odd.ib === 0) || rates[0];
            const yes = rates.find(isBack) || rates[1];
            const position = asList(positions).find((item) => sameId(item.selectionId, fancy.selectionId) &&
              (!item.fancyName || item.fancyName === name))?.position ?? fancy.position;
            return <tr key={`${fancy.selectionId}-${name}`} className="fancybet">
              <th colSpan={3}>
                <dl className="fancy-th-layout">
                  <dt><p>{name}</p></dt>
                  {position != null && position !== "" && <dd className="dd-tips">
                    <Position value={position} />
                    <ul className="fancy-tips"><li><button type="button" className="btn-book" onClick={() => onBook({ ...fancy, fancyName: name })}>Book</button></li></ul>
                  </dd>}
                </dl>
              </th>
              <td colSpan={2} className="multi_select">
                {isSuspended(fancy.odds?.ms ?? fancy.ms) && <div className="fancy-suspend"><span>Suspend</span></div>}
                <ul>
                  <li className="lay-1"><strong className="analytics-rate">{no?.rt ?? "-"}<span>{no?.pt ?? ""}</span></strong></li>
                  <li className="back-1"><strong className="analytics-rate">{yes?.rt ?? "-"}<span>{yes?.pt ?? ""}</span></strong></li>
                </ul>
              </td>
              <td colSpan={2} className="td-fancy_merge" />
            </tr>;
          })}
        </tbody>}
      </table>
    </div>
  </section>;
}

